'use client';

/**
 * Contact Us Page
 * ===============
 *
 * Contact page with liquid ether background effect.
 * Features a contact form with validation and email integration.
 *
 * FEATURES:
 * - Zod schema validation for all form fields
 * - Web3Forms integration for sending emails to magnategroup1000@gmail.com
 * - WhatsApp follow-up modal after submission for faster response
 * - Glass morphism styled form and info cards
 * - Responsive grid layout
 * - Pre-filled message support via URL query parameter
 *
 * DESIGN:
 * - LiquidEther background for the liquid glass theme
 * - Glass morphism styled form and info cards
 * - Responsive grid layout
 */

import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import LazyLiquidEther from '@/components/backgrounds/LazyLiquidEther';
import ContactForm from '@/components/contact/ContactForm';

function ContactContent() {
  const searchParams = useSearchParams();
  const initialMessage = searchParams.get('message') || '';

  return <ContactForm initialMessage={initialMessage} />;
}

export default function ContactPage() {
  return (
    <main className="min-h-screen relative overflow-hidden">
      {/* Liquid Ether Background */}
      <div className="fixed inset-0 z-0">
        <LazyLiquidEther
          colors={['#D4A574', '#8B6914', '#C9A227']}
          mouseForce={20}
          cursorSize={100}
          resolution={0.5}
          isViscous={false}
          viscous={30}
          autoDemo={true}
          autoSpeed={0.5}
          autoIntensity={2.2}
          autoResumeDelay={2000}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 pt-32 px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-white text-center mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-white/70 text-center max-w-2xl mx-auto mb-12">
            We&apos;d love to hear from you. Reach out to schedule a site visit or learn more.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Contact Form */}
            <div className="glass-card rounded-3xl p-8 flex flex-col">
              <h2 className="text-2xl font-semibold text-white mb-6">Send us a message</h2>
              {/* Fallback is the real form (without the ?message= prefill) so the
                  prerendered HTML already has the full-height form. The old
                  one-line "Loading form..." fallback caused a 0.3 CLS jump. */}
              <Suspense fallback={<ContactForm />}>
                <ContactContent />
              </Suspense>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col gap-4">
              <div className="glass-card rounded-3xl p-6 flex-1">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-amber-400/30 rounded-full flex items-center justify-center text-sm">📍</span>
                  Address
                </h3>
                <p className="text-white/70 ml-10">
                  Magnate Regalia<br />
                  B/H Science City, Nr. S.P. Ring Road<br />
                  Ahmedabad - 380059
                </p>
              </div>

              <div className="glass-card rounded-3xl p-6 flex-1">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-amber-400/30 rounded-full flex items-center justify-center text-sm">📞</span>
                  Phone
                </h3>
                <p className="text-white/70 ml-10">
                  <a href="tel:+919664805202" className="hover:text-white transition-colors">+91 96648 05202</a>
                </p>
              </div>

              <div className="glass-card rounded-3xl p-6 flex-1">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-amber-400/30 rounded-full flex items-center justify-center text-sm">✉️</span>
                  Email
                </h3>
                <p className="text-white/70 ml-10">
                  <a href="mailto:magnategroup1000@gmail.com" className="hover:text-white transition-colors">magnategroup1000@gmail.com</a>
                </p>
              </div>

              <div className="glass-card rounded-3xl p-6 flex-1">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-amber-400/30 rounded-full flex items-center justify-center text-sm">🕐</span>
                  Working Hours
                </h3>
                <p className="text-white/70 ml-10">
                  Monday - Sunday: 10:00 AM - 7:00 PM
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
